import type {
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError, AxiosRequestConfig } from "axios";

import type { IQuery } from "@citrine/client";
import {
  CursorClient,
  RefreshClient,
  useRevertableQuery,
} from "@citrine/client";
import { queryClient } from "@citrine/client/utils";

import type { JobExecutionError, PollingOptions } from "../job-execution";
import { JobExecutionClient } from "../job-execution";
import { MaterialHistoryClient } from "../material-history";

import type {
  BatchDeleteResponse,
  DataObjectApiError,
  IBatchSaveRequest,
} from "./data-object-api";
import { BatchOperationError, getErrorsFromResponse } from "./data-object-api";
import { CONCEPT_ENDPOINTS } from "./data-object-endpoints";
import type {
  DataConceptType,
  DataObjectOrTemplateType,
  IAttributeTemplate,
  IDataObjectOrTemplate,
  IIngredientRun,
  IIntegerBounds,
  ILinkByUid,
  IMaterialTemplate,
  IMeasurementTemplate,
  IObjectTemplate,
  IProcessTemplate,
  IRealBounds,
} from "./types";

interface ScopedIdentifier {
  id: string;
  scope?: string;
}

interface TypedIdentifier extends ScopedIdentifier {
  type: DataConceptType;
}

export const isScopedIdentifier = (
  id: OptionallyScopedIdentifier
): id is ScopedIdentifier => {
  return typeof id !== "string";
};

export const isTypedIdentifier = (
  id: OptionallyScopedIdentifier
): id is TypedIdentifier => {
  return isScopedIdentifier(id) && "type" in id;
};

export type OptionallyScopedIdentifier =
  | string
  | ScopedIdentifier
  | TypedIdentifier;

export type DataObjectQuery = IQuery & {
  /**
   * Both of these attributes need to be set to `false` in order to do infinite
   * scroll from newest to oldest. If `ascending` is set to `false` but `forward`
   * is not, the results will come back in the correct order, but there will only
   * be a `prev` attribute for that first "page" of results. We expect a `next`
   * attribute, which is achieved by also setting `forward: false`.
   *
   * https://citrine.atlassian.net/browse/PLA-11032?focusedCommentId=67350
   */
  ascending?: boolean;
  forward?: boolean;
} & (
    | {
        filter_type: "Bounds";
        concept_type?: DataObjectOrTemplateType;
        template_id: string;
        lower_bound: number | string;
        upper_bound: number | string;
        default_units: string;
        bound_type: IRealBounds["type"] | IIntegerBounds["type"];
      }
    | {
        filter_type: "ID";
        concept_type?: DataObjectOrTemplateType;
        id: string;
        scope?: string;
      }
    | {
        filter_type: "Name";
        concept_type?: DataObjectOrTemplateType;
        name: string;
        dataset_id?: string;
      }
    | {
        filter_type: "Tags";
        concept_type?: DataObjectOrTemplateType;
        tags?: string;
        dataset_id?: string;
      }
    | {
        filter_type?: never;
        concept_type?: DataConceptType | "all";
      }
  );

export interface LoadAllTemplatesResults {
  properties: IAttributeTemplate[];
  parameters: IAttributeTemplate[];
  conditions: IAttributeTemplate[];
  materials: IObjectTemplate[];
  measurements: IObjectTemplate[];
  process: IObjectTemplate[];
}

export class DataObjectUpdateClient extends RefreshClient {
  protected constructor(
    protected readonly endpoint: string,
    protected readonly materialHistoryClient?: MaterialHistoryClient,
    protected readonly onBatchSave?: (payload: IDataObjectOrTemplate[]) => void
  ) {
    super();
  }

  static getInstanceForTeam(teamId: string, datasetId: string) {
    return new DataObjectUpdateClient(
      `/api/v1/teams/${teamId}/datasets/${datasetId}`,
      MaterialHistoryClient.getInstance(teamId, datasetId)
    );
  }

  async batchSave({
    payload,
    dryRun = false,
  }: IBatchSaveRequest): Promise<IDataObjectOrTemplate[]> {
    try {
      const response = await this.request<{
        objects: IDataObjectOrTemplate[];
      }>({
        method: "PUT",
        url: `${this.endpoint}/storables/batch`,
        params: { dry_run: dryRun },
        data: { objects: payload },
      });

      return response.objects;
    } catch (error) {
      const errors = getErrorsFromResponse(error);
      throw new BatchOperationError("Error during batch save", errors);
    }
  }

  useBatchSave(
    options?: Omit<
      UseMutationOptions<
        IDataObjectOrTemplate[],
        BatchOperationError,
        IBatchSaveRequest
      >,
      "mutationFn"
    >
  ) {
    return useMutation({
      ...options,
      mutationFn: (saveRequest) => this.batchSave(saveRequest),
    });
  }
}

export abstract class DataObjectClient extends CursorClient<
  IDataObjectOrTemplate,
  OptionallyScopedIdentifier
> {
  protected static v1Api: string = "/api/v1";
  protected static v2Api: string = "/api/v2";

  protected constructor(
    protected readonly resourcePath: string,
    protected readonly datasetId: string
  ) {
    super({
      endpoint: `${DataObjectClient.v1Api}/${resourcePath}/storables`,
      defaultQuery: {
        per_page: 200,
        cursor: undefined,
        ascending: undefined,
        forward: undefined,
        filter_type: undefined,
        concept_type: undefined,
        bound_type: undefined,
        dataset_id: undefined,
        default_units: undefined,
        id: undefined,
        lower_bound: undefined,
        name: undefined,
        scope: undefined,
        tags: undefined,
        template_id: undefined,
        upper_bound: undefined,
      },
      findField: "contents",
      idField: "uids.id",
    });
  }

  static getInstanceForProject(projectId: string): ReadonlyDataObjectClient;
  static getInstanceForProject(
    projectId: string,
    datasetId: string
  ): DataObjectClient;
  static getInstanceForProject(projectId: string, datasetId?: string) {
    return datasetId
      ? ProjectDataObjectClient.getInstance(projectId, datasetId)
      : ReadonlyDataObjectClient.getInstanceForProject(projectId);
  }

  static getInstanceForTeam(teamId: string, datasetId: string) {
    return ReadonlyDataObjectClient.getInstanceForTeam(teamId, datasetId);
  }

  toModel(payload: any) {
    const model = super.toModel(payload);
    model.persisted = true;
    return model;
  }

  async find(query: DataObjectQuery) {
    try {
      return await super.find(query);
    } catch (error) {
      // find by ID wraps the get URI response as an array
      // need to catch 404 errors and treat as an empty array
      if (query.filterType === "ID" && error.response.status === 404) {
        return [];
      }
      throw error;
    }
  }

  invalidateCache() {
    this.invalidateAfterMutation();
  }

  getUri(id?: OptionallyScopedIdentifier) {
    return typeof id === "string"
      ? `${this.endpoint}/id/${id}`
      : `${this.endpoint}/${id?.scope || "id"}/${id?.id}`;
  }

  async batchGet<T extends IDataObjectOrTemplate>(
    uids: ILinkByUid[]
  ): Promise<T[]> {
    const response = await this.request<{ results: T[] }>({
      method: "POST",
      url: `${DataObjectClient.v2Api}/${this.resourcePath}/gemd/batch-get`,
      data: { ids: uids },
    });

    return response.results.map(
      (obj) =>
        ({
          ...obj,
          persisted: true,
        }) as T
    );
  }

  /**
   * This will send the list of UIDs to the API to be deleted. The backend will handle deleting
   * in the correct order or will return failure responses for any objects it can't delete
   * @param uids a list of Citrine IDs that are converted into ILinkByUids and then sent for deletion
   */
  async batchDelete(uids: string[]): Promise<BatchDeleteResponse> {
    try {
      const response = await this.request<BatchDeleteResponse>({
        method: "POST",
        url: `${DataObjectClient.v1Api}/${this.resourcePath}/gemd/batch-delete`,
        data: {
          ids: uids.map((uid) => ({
            id: uid,
            scope: "id",
            type: "link_by_uid",
          })),
        },
      });

      queryClient.invalidateQueries({
        queryKey: ["batch-get", this.resourcePath],
      });
      this.invalidateAfterMutation();

      return response;
    } catch (error) {
      const errors = getErrorsFromResponse(error);
      throw new BatchOperationError("Error during batch delete", errors);
    }
  }

  /**
   * React query wrapper around batchDelete.
   * @param options React query options to forward to the useMutation hook
   */
  useBatchDelete(
    options?: Omit<
      UseMutationOptions<BatchDeleteResponse, BatchOperationError, string[]>,
      "mutationFn"
    >
  ): UseMutationResult<BatchDeleteResponse, BatchOperationError, string[]> {
    return useMutation({
      ...options,
      mutationFn: (ids) => this.batchDelete(ids),
    });
  }

  useBatchGet<T extends IDataObjectOrTemplate>(
    uids: ILinkByUid[],
    options: Omit<UseQueryOptions<T[]>, "queryKey" | "queryFn"> = {}
  ): UseQueryResult<T[]> {
    return useQuery({
      ...options,
      queryKey: [
        "batch-get",
        this.resourcePath,
        ...uids.map(({ id }) => id).sort(),
      ],
      queryFn: () => this.batchGet<T>(uids),
    });
  }

  /**
   * This will attempt to look up related data objects, for a list of all supported relations, see the API docs
   * https://docs.corp.citrine.io./platform-backend/data-objects/index.html#tag/DataObjects
   * @param id ID to check of the object to check related data object types for (concepts)
   * @param baseConcept the type of object for the id passed in
   * @param relatedConcept the type of data object that you would like to look up relationships for
   */
  async getRelatedConcepts<T>(
    id: OptionallyScopedIdentifier,
    baseConcept: DataConceptType = "material_run",
    relatedConcept: DataConceptType = "ingredient_run"
  ): Promise<T> {
    const scopeIdUrlPart = isScopedIdentifier(id)
      ? `${id.scope ?? "id"}/${id.id}`
      : `id/${id}`;
    const url = `${DataObjectClient.v1Api}/${this.resourcePath}/${CONCEPT_ENDPOINTS[baseConcept]}/${scopeIdUrlPart}/${CONCEPT_ENDPOINTS[relatedConcept]}`;
    const response = await this.request<{ contents: T }>({
      url,
      method: "GET",
    });
    return response.contents;
  }

  useGetRelatedConcepts(
    id: OptionallyScopedIdentifier,
    baseConcept: DataConceptType = "material_run",
    relatedConcept: DataConceptType = "ingredient_run",
    options?: Omit<UseQueryOptions, "queryKey" | "queryFn">
  ) {
    const scopeIdUrlPart = isScopedIdentifier(id)
      ? `${id.scope ?? "id"}/${id.id}`
      : `id/${id}`;
    return useQuery({
      ...options,
      queryKey: [
        `${DataObjectClient.v1Api}/${this.resourcePath}/${CONCEPT_ENDPOINTS[baseConcept]}/${scopeIdUrlPart}/${CONCEPT_ENDPOINTS[relatedConcept]}`,
        baseConcept,
        relatedConcept,
      ],
      queryFn: () => this.getRelatedConcepts(id, baseConcept, relatedConcept),
    });
  }

  /**
   * Checks to see which ingredient runs this material is used in (which other processes this material is ultimately used in).
   * @param materialUid
   */
  async getRelatedIngredientRuns(
    materialUid: OptionallyScopedIdentifier
  ): Promise<{
    materialInputIngredients: IIngredientRun[];
    processInputIngredients: IIngredientRun[];
  }> {
    return {
      materialInputIngredients:
        await this.getRelatedConcepts<IIngredientRun[]>(materialUid),
      // leaving this for now as there is a desire to make rendering an entire node's history optional so we would need to populate this when atetmpting to delete
      processInputIngredients: [],
    };
  }

  /**
   * This will load all object and attribute templates that a user can view.
   */
  async loadAllTemplates(): Promise<LoadAllTemplatesResults> {
    const [
      parameters,
      conditions,
      properties,
      materials,
      measurements,
      process,
    ] = await Promise.all(
      [
        "parameter_template",
        "condition_template",
        "property_template",
        "material_template",
        "measurement_template",
        "process_template",
      ].map((conceptType: DataConceptType) =>
        this.findAll({ conceptType, per_page: 200 })
      )
    );

    return {
      parameters: parameters as IAttributeTemplate[],
      conditions: conditions as IAttributeTemplate[],
      properties: properties as IAttributeTemplate[],
      materials: materials as IMaterialTemplate[],
      measurements: measurements as IMeasurementTemplate[],
      process: process as IProcessTemplate[],
    };
  }

  useLoadAllTemplates(
    options?: Omit<
      UseQueryOptions<LoadAllTemplatesResults>,
      "queryKey" | "queryFn"
    >
  ) {
    return useRevertableQuery<LoadAllTemplatesResults, AxiosError>({
      ...options,
      queryKey: [this.resourcePath, "load-all-templates"],
      queryFn: () => this.loadAllTemplates(),
    });
  }

  protected findConfig({
    concept_type = "all",
    filter_type = "Tags",
    ...query
  }: DataObjectQuery): AxiosRequestConfig {
    // Set dataset_id to undefined so it gets stripped if datasetId is ""
    query.dataset_id = this.datasetId || undefined;

    switch (filter_type) {
      case "Bounds":
        return {
          method: "POST",
          url: `${DataObjectClient.v2Api}/${this.resourcePath}/${CONCEPT_ENDPOINTS[concept_type]}/filter-by-attribute-bounds`,
          data: {
            attribute_bounds: {
              [query.templateId as string]: query,
            },
          },
        };
      case "ID":
        return {
          method: "GET",
          url: this.getUri(query as ScopedIdentifier),
          params: this.normalizeQuery(query),
          transformResponse: (content) => ({ contents: [JSON.parse(content)] }),
        };
      case "Name":
        return {
          method: "GET",
          url: `${DataObjectClient.v2Api}/${this.resourcePath}/${CONCEPT_ENDPOINTS[concept_type]}/filter-by-name`,
          params: this.normalizeQuery(query),
        };
      case "Tags":
      default:
        return {
          method: "GET",
          url: `${DataObjectClient.v2Api}/${this.resourcePath}/${CONCEPT_ENDPOINTS[concept_type]}`,
          params: this.normalizeQuery({
            ...query,
            // bugfix for API to prevent empty query param
            tags: query.tags || undefined,
          }),
        };
    }
  }

  protected createConfig(model: IDataObjectOrTemplate): AxiosRequestConfig {
    return {
      method: "POST",
      url: `${DataObjectClient.v1Api}/${this.resourcePath}/datasets/${this.datasetId}/${CONCEPT_ENDPOINTS[model.type]}`,
      data: this.fromModel(model),
    };
  }

  protected deleteConfig(id: OptionallyScopedIdentifier): AxiosRequestConfig {
    if (isTypedIdentifier(id)) {
      return {
        method: "DELETE",
        url: `${DataObjectClient.v1Api}/${this.resourcePath}/datasets/${this.datasetId}/${
          CONCEPT_ENDPOINTS[id.type]
        }/${id.scope ?? "id"}/${id.id}`,
      };
    }
    throw new Error("Object Type is required to delete data objects");
  }

  protected updateConfig(model: IDataObjectOrTemplate): AxiosRequestConfig {
    return {
      ...super.updateConfig(model),
      url: `${DataObjectClient.v1Api}/${this.resourcePath}/datasets/${model.dataset}/${
        CONCEPT_ENDPOINTS[model.type]
      }/id/${this.getId(model)}`,
    };
  }
}

class ReadonlyDataObjectClient extends DataObjectClient {
  create: never;
  useCreate: never;
  updateAsync: never;
  useUpdateAsync: never;
  delete: never;
  useDelete: never;

  static getInstanceForProject(projectId: string) {
    return new this(`projects/${projectId}`, "");
  }

  static getInstanceForTeam(teamId: string, datasetId: string) {
    return new this(`teams/${teamId}`, datasetId);
  }
}

class ProjectDataObjectClient extends DataObjectClient {
  protected constructor(
    protected readonly projectId: string,
    protected readonly datasetId: string
  ) {
    super(`projects/${projectId}`, datasetId);
  }

  static getInstance(projectId: string, datasetId: string) {
    return new this(projectId, datasetId);
  }

  /**
   * Different types of data objects (templates in particular) can not be updated
   * using a put request. If certain properties on objects are changed, they will
   * be rejected instantly with a mutation exception. This method calls the "async"
   * version to update data objects. See:  https://docs.corp.citrine.io./platform-backend/data-objects/index.html#tag/DataObjects/paths/~1api~1v1~1datasets~1{dataset_id}~1{object-type-plural}~1{scope}~1{id}~1async/put
   * @param object The data object to update
   * @returns the object that's being updated
   */
  async updateAsync<T extends IDataObjectOrTemplate>(
    object: T,
    pollingOptions?: PollingOptions
  ): Promise<T> {
    const { job_id } = await this.request<{ job_id: string }>({
      method: "PUT",
      url: `${DataObjectClient.v1Api}/${this.resourcePath}/datasets/${this.datasetId}/${
        CONCEPT_ENDPOINTS[object.type]
      }/id/${object.uids.id}/async`,
      data: object,
    });

    await JobExecutionClient.getInstanceForProject(this.projectId).poll(
      job_id,
      pollingOptions
    );
    this.invalidateAfterMutation(object.uids.id);
    return object;
  }

  /**
   * Wraps the updateAsync method in a useMutation hook.
   */
  useUpdateAsync<T extends IDataObjectOrTemplate>(
    options?: Omit<
      UseMutationOptions<
        T,
        JobExecutionError | AxiosError<DataObjectApiError>,
        T
      >,
      "mutationFn"
    > &
      PollingOptions
  ) {
    return useMutation({
      ...options,
      mutationFn: (object) => this.updateAsync(object, options),
    });
  }
}
